/**
 * `/spaces` — entry point. Sends the user to their first space, or
 * creates a default ("My space") when they have none, so the nav entry
 * never lands on a dead end.
 *
 * Spaces are site-agnostic to view — the active site is a lens the
 * canvas resolves every card against — so this doesn't filter by
 * site: one space follows the user across site switches.
 *
 * Creating a space needs a site to seed against (`site_uuid` is
 * required on `useCreateSpace`), so a zero-sites org can never clear
 * the create-branch below — without the `noSites` check this used to
 * sit on "Loading your spaces…" forever instead of ever prompting the
 * user to add a site.
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { Button } from '@/components/ui/button';
import { useActiveSite } from '@/lib/active-site';
import { useCreateSpace, useSpaces } from '@/lib/queries/canvas';
import { uiDialogs } from '@/lib/stores/ui-dialogs';
import { Globe, Plus } from '@phosphor-icons/react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef } from 'react';

export const Route = createFileRoute('/_app/spaces/')({
  // Disabled surface — redirect any direct access to /performance.
  beforeLoad: () => {
    throw redirect({ to: '/performance' });
  },
  component: SpacesIndex,
});

function SpacesIndex(): ReactElement {
  const { uuid: siteUuid, noSites } = useActiveSite();
  const spaces = useSpaces(null);
  const create = useCreateSpace();
  const navigate = useNavigate();
  // Guard against double-firing (creating two default spaces) across
  // the success render + the create mutation settling.
  const acted = useRef(false);

  useEffect(() => {
    if (acted.current || !spaces.isSuccess) return;
    const rows = spaces.data;
    if (rows.length > 0) {
      acted.current = true;
      void navigate({
        to: '/spaces/$spaceUuid',
        params: { spaceUuid: rows[0]!.uuid },
        replace: true,
      });
      return;
    }
    // Creating needs a site to seed against — wait for it to resolve.
    if (siteUuid && !create.isPending) {
      acted.current = true;
      create.mutate(
        { name: 'My space', site_uuid: siteUuid },
        {
          onSuccess: (s) =>
            navigate({ to: '/spaces/$spaceUuid', params: { spaceUuid: s.uuid }, replace: true }),
        },
      );
    }
  }, [siteUuid, spaces.isSuccess, spaces.data, create, navigate]);

  if (noSites && spaces.isSuccess && spaces.data.length === 0) {
    return (
      <EmptyState
        icon={Globe}
        title="Add a site to start building spaces"
        description="Spaces need at least one site to seed a board against. Add your first site, then come back here."
        action={
          <Button onClick={() => uiDialogs.open('add-site')}>
            <Plus aria-hidden="true" />
            Add site
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex h-full items-center justify-center text-sm text-[var(--color-mute)]">
      Loading your spaces…
    </div>
  );
}
